import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import Can from '../../components/permissions/Can';
import api from '../../api/config';
import { MERCHANT_SERVICE_PROVIDERS } from '../../api/endpoints';
import { toast } from 'react-toastify';
import {
  buildMerchantServiceProviderRelationParams,
  formatMerchantServiceProviderDate,
  getMerchantServiceProviderAuthorLabel,
  getMerchantServiceProviderStatusLabel,
} from './merchantServiceProviderApiHelpers';
import { MERCHANT_SERVICE_PROVIDER_PERMISSIONS } from './merchantServiceProviderPermissions';

const row = (label, value) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Grid>
);

const ViewMerchantServiceProvider = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(MERCHANT_SERVICE_PROVIDERS.BY_ID(id), {
          params: buildMerchantServiceProviderRelationParams(),
        });
        const payload = data?.data ?? data;
        if (!cancelled) setRecord(payload);
      } catch (err) {
        toast.error(
          err.response?.data?.message ||
            'Failed to load merchant service provider'
        );
        navigate('/merchant-service-providers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading || !record) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/merchant-service-providers')}
          sx={{ mb: 2 }}
        >
          Back to merchant service providers
        </Button>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {record.name ?? 'Merchant Service Provider'}
          </Typography>
          <Can permission={MERCHANT_SERVICE_PROVIDER_PERMISSIONS.EDIT}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(`/merchant-service-providers/edit/${id}`)}
            >
              Edit
            </Button>
          </Can>
        </Box>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {row('Name', record.name)}
              {row('Status', getMerchantServiceProviderStatusLabel(record))}
              {row('Author', getMerchantServiceProviderAuthorLabel(record))}
              {row('Created at', formatMerchantServiceProviderDate(record.created_at))}
              {row('Description', record.description ?? '—')}
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewMerchantServiceProvider;
